import {
    faArrowUpRightDots,
    faCheck,
    faCircleQuestion,
    faLanguage,
    faPuzzlePiece,
    faSliders,
    faTags,
    faTimer,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@sharedComponents/Button';
import Divider from '@sharedComponents/Divider';
import FlexBox from '@sharedComponents/FlexBox';
import { InputRadio } from '@sharedComponents/InputRadio';
import InputSelect from '@sharedComponents/InputSelect';
import Spoiler from '@sharedComponents/Spoiler';
import Tooltip from '@sharedComponents/Tooltip';
import Typography from '@sharedComponents/Typography';
import { getTranslatedString } from '@src/i18n';
import { effectiveAverageDuration } from '@utils/duration';
import { stripHtmlTags } from '@utils/html';
import { LanguageNames } from '@utils/languageNames';
import cx from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import * as style from './style.module.css';

const TestSheet = ({ test, onClose, onConfirm, disabled, selectedTests = [], subtestId = null, readOnly = false }) => {
    const { t, i18n } = useTranslation();

    const isAudioDescription = test.tests[0]?.testNature === 23;
    const interfaceLanguage = i18n.language;

    // Same category derivation as BookingTestCard: the type prefix of the name for
    // 9011 / audio-description tests, the hands-on simulation label for nature 1.
    const testType = getTranslatedString(test.name)?.split('-')[0].trim();
    const hasCategoryNineTests = test.tests.some((level) => level.categoryId === 9011);
    const hasTestNature23 = test.tests.some((level) => level.testNature === 23);
    const hasTestNature1 = test.tests.some((level) => level.testNature === 1);
    const categoryText =
        hasCategoryNineTests || hasTestNature23 || hasTestNature1
            ? [hasCategoryNineTests && testType, hasTestNature23 && testType, hasTestNature1 && t('Hands-on simulation')]
                  .filter(Boolean)
                  .join('')
            : null;
    const languageNames = useMemo(() => LanguageNames(t), [t]);
    const [selectedLevel, setSelectedLevel] = useState(
        test.tests.length === 1
            ? test.tests[0]
            : test.tests.find((level) => level.isSelected) || test.tests.find((level) => level.isDefault),
    );
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [testLevelsByLanguage, setTestLevelsByLanguage] = useState(null);

    useEffect(() => {
        // if multiple tests, get all the possible languages, otherwise select the language of the single test
        if (test.tests.length >= 1) {
            // A level without a language must not create a selector entry: it would
            // propose a language whose level list comes out empty.
            const languages = new Set(test.tests.map((test) => test.language).filter(Boolean));
            const languageOrder = Object.keys(languageNames);
            const languageOptions = [...languages]
                .sort((a, b) => {
                    // Sort languages based on their order in languageNames, with a fallback to alphabetical order
                    const indexA = languageOrder.indexOf(a);
                    const indexB = languageOrder.indexOf(b);
                    // Put languages that exist in languageNames first, in order
                    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                    if (indexA !== -1) return -1;
                    if (indexB !== -1) return 1;
                    // If neither is in languageNames, sort alphabetically
                    return a.localeCompare(b);
                })
                .map((lang) => ({
                    value: lang,
                    label: languageNames[lang] || lang.toUpperCase(),
                }));
            setAvailableLanguages(languageOptions);
            // Object.entries convert the object returned by reduce to an array of { language, tests } objects
            const testsByLanguage = Object.entries(
                test.tests.reduce((acc, test) => {
                    const language = test.language;
                    // if language not already in acc, add it with an empty array
                    if (!acc[language]) {
                        acc[language] = [];
                    }
                    acc[language].push(test);
                    return acc;
                }, {}),
            ).map(([language, tests]) => ({ language, tests }));

            setTestLevelsByLanguage(testsByLanguage);

            if (languageOptions.length > 0) {
                // Set the first language as selected by default
                const interfaceLanguageOption = languageOptions.find((option) => option.value === interfaceLanguage);
                const defaultOption = interfaceLanguageOption || languageOptions[0];
                setSelectedLanguage(defaultOption.value);
                setAvailableLanguages(
                    languageOptions.map((o) => (o.value === defaultOption.value ? { ...o, subLabel: t('(default)') } : o)),
                );

                // Auto-select if only one test for the default language
                const testsForDefaultLanguage = testsByLanguage.find(({ language }) => language === defaultOption.value)?.tests;
                if (testsForDefaultLanguage && testsForDefaultLanguage.length === 1) {
                    setSelectedLevel(testsForDefaultLanguage[0]);
                }
            }
        } else {
            setSelectedLanguage(test.tests[0]?.language);
            setTestLevelsByLanguage(test.tests[0]?.language ? [{ language: test.tests[0]?.language, tests: test.tests }] : []);
        }
    }, [test.tests, setAvailableLanguages, setSelectedLanguage, setTestLevelsByLanguage, languageNames, interfaceLanguage, t]);

    const handleSelectChange = (selectedOption) => {
        if (selectedOption) {
            setSelectedLanguage(selectedOption.value);
            setSelectedLevel(null); // reset selected level when changing language

            // Auto-select if only one test for the selected language
            const testsForLanguage = testLevelsByLanguage?.find(({ language }) => language === selectedOption.value)?.tests;
            if (testsForLanguage && testsForLanguage.length === 1) {
                setSelectedLevel(testsForLanguage[0]);
            }
        }
    };

    const isLevelAlreadySelected = (level) => selectedTests.some((t) => t.id === level.id);

    // Always null in read-only mode: the sheet pre-selects the default level for the
    // booking flow, which must not read as a choice when only browsing.
    const highlightedLevelId = readOnly ? null : selectedLevel?.id;

    /**
     * Interaction props of a level card, empty in read-only mode so the card does not
     * behave like a button. Shared by the two rendering branches so they cannot drift.
     */
    const levelInteractionProps = (level) =>
        readOnly
            ? {}
            : {
                  role: 'button',
                  tabIndex: isLevelAlreadySelected(level) ? -1 : 0,
                  'aria-disabled': isLevelAlreadySelected(level),
                  onClick: () => {
                      if (isLevelAlreadySelected(level)) return;
                      setSelectedLevel(level);
                  },
                  onKeyDown: (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                          if (isLevelAlreadySelected(level)) return;
                          e.preventDefault();
                          setSelectedLevel(level);
                      }
                  },
              };

    const averageDuration = useMemo(
        () => (test.tests.length ? test.tests.reduce((acc, level) => acc + effectiveAverageDuration(level), 0) / test.tests.length : 0),
        [test.tests],
    );
    const maxDuration = useMemo(
        () => (test.tests.length ? Math.max(...test.tests.map(({ maxDuration }) => maxDuration)) : 0),
        [test.tests],
    );

    const spoilerText = [
        getTranslatedString(test.description),
        test.tests.length === 1 ? getTranslatedString(test.tests[0].description) : null,
    ]
        .map((description) => stripHtmlTags(description).trim())
        .filter(Boolean)
        .join('\n\n');

    return (
        <div className={style.container}>
            <div className={style.header}>
                <Typography fontSize="xl" fontWeight="semi-bold">
                    {getTranslatedString(test.name)}
                </Typography>
                {test.tags?.find((tag) => tag.type === 'domain') && (
                    <div className={style.tags}>
                        <FontAwesomeIcon icon={faTags} className={style.tagsIcon} />
                        <div>{getTranslatedString(test.tags.find((tag) => tag.type === 'domain').name)}</div>
                    </div>
                )}
            </div>

            {spoilerText.length > 0 ? (
                <Spoiler
                    text={spoilerText}
                    maxLines={5}
                    classNames={{
                        text: style.levelsDescription,
                    }}
                />
            ) : (
                <Typography fontSize="s" fontWeight="regular" color={'neutral-primary'}>
                    {t('No description')}
                </Typography>
            )}

            <div className={style.features}>
                {!!test.tests.length && (
                    <>
                        {!!(averageDuration || maxDuration) && (
                            <div className={style.featuresLine}>
                                <FontAwesomeIcon icon={faTimer} className={style.featuresIcon} />
                                <Typography fontSize="s" fontWeight="regular">
                                    {maxDuration ? (
                                        <Trans
                                            t={t}
                                            i18nKey="<0>Average duration:</0> {{averageDuration}} minutes - <0>Maximum duration:</0> {{maxDuration}} minutes"
                                            values={{
                                                averageDuration: Math.floor(averageDuration / 60) || '-',
                                                maxDuration: Math.floor(maxDuration / 60),
                                            }}
                                            components={{ 0: <Typography span fontSize="s" fontWeight="medium" /> }}
                                        />
                                    ) : (
                                        <Trans
                                            t={t}
                                            i18nKey="<0>Average duration:</0> {{averageDuration}} minutes"
                                            values={{ averageDuration: Math.floor(averageDuration / 60) || '-' }}
                                            components={{ 0: <Typography span fontSize="s" fontWeight="medium" /> }}
                                        />
                                    )}
                                </Typography>
                            </div>
                        )}

                        <div className={style.featuresLine}>
                            <FontAwesomeIcon icon={faLanguage} className={style.featuresIcon} />
                            <Typography fontSize="s" fontWeight="regular">
                                <Trans
                                    t={t}
                                    i18nKey={
                                        availableLanguages.length > 1
                                            ? '<0>Available languages:</0> {{availableLanguages}}'
                                            : '<0>Available language:</0> {{availableLanguages}}'
                                    }
                                    values={{
                                        availableLanguages: availableLanguages.map(({ label }) => label).join(' - '),
                                    }}
                                    components={{ 0: <Typography span fontSize="s" fontWeight="medium" /> }}
                                />
                            </Typography>
                        </div>
                    </>
                )}

                {test.tests.length === 1 && test.tests[0]?.title && (
                    <div className={style.featuresLine}>
                        <FontAwesomeIcon icon={faArrowUpRightDots} className={style.featuresIcon} />
                        <Typography fontSize="s" fontWeight="regular">
                            <Trans
                                t={t}
                                i18nKey="<0>Level :</0> {{levelName}}"
                                values={{
                                    levelName: getTranslatedString(test.tests[0]?.title ?? '-'),
                                }}
                                components={{ 0: <Typography span fontSize="s" fontWeight="medium" /> }}
                            />
                        </Typography>
                    </div>
                )}

                {categoryText && (
                    <div className={style.featuresLine}>
                        <FontAwesomeIcon icon={faPuzzlePiece} className={style.featuresIcon} />
                        <Typography fontSize="s" fontWeight="regular">
                            {categoryText}
                        </Typography>
                    </div>
                )}
            </div>

            {test.tests.length > 1 && (
                <>
                    <Divider />

                    <div className={style.featuresLine}>
                        <FontAwesomeIcon
                            icon={readOnly ? faArrowUpRightDots : faSliders}
                            className={cx(style.featuresIcon, style.accent)}
                        />
                        <Typography fontSize="l" fontWeight="semi-bold">
                            {readOnly ? t('Available levels') : t('Configure your test')}
                        </Typography>
                    </div>

                    {/* The language is already stated in the features block above. The
                        selector below stays: it reaches the other languages' levels. */}
                    {!readOnly && (
                        <FlexBox flexDirection="row" className={style.language} gap={'xs'}>
                            <FlexBox flexDirection="column" alignItems="center">
                                {selectedLanguage && (
                                    <div className={style.done}>
                                        <FontAwesomeIcon icon={faCheck} />
                                    </div>
                                )}
                                {!selectedLanguage && <div className={style.number}>1</div>}
                            </FlexBox>
                            <Typography
                                fontSize="m"
                                fontWeight="regular"
                                color={selectedLanguage ? 'branded-secondary' : 'neutral-secondary'}
                            >
                                {availableLanguages?.length === 1
                                    ? t(
                                          isAudioDescription
                                              ? 'Language of the audio description : {{language}}'
                                              : 'Language of the test : {{language}}',
                                          { language: languageNames[selectedLanguage] },
                                      )
                                    : t(isAudioDescription ? 'Language of the audio description :' : 'Language of the test :')}
                            </Typography>
                        </FlexBox>
                    )}
                    {/* The selector stays in read-only: it only lists languages carrying
                        at least one level, and browsing them is part of the viewing. */}
                    {availableLanguages?.length > 1 && (
                        <div className={style.inputLanguage}>
                            {test.tests.length > 1 && availableLanguages?.length > 1 && (
                                <InputSelect
                                    options={availableLanguages}
                                    onChange={handleSelectChange}
                                    value={availableLanguages.find((o) => o.value === selectedLanguage)}
                                    isSearchable={false}
                                />
                            )}
                        </div>
                    )}
                    {!readOnly && (
                        <FlexBox flexDirection="row" gap={'xs'}>
                            <FlexBox flexDirection="column" alignItems="center">
                                {selectedLevel && (
                                    <div className={style.done}>
                                        <FontAwesomeIcon icon={faCheck} />
                                    </div>
                                )}
                                {!selectedLevel && <div className={style.number}>2</div>}
                            </FlexBox>
                            <Typography fontSize="m" fontWeight="regular" color={selectedLevel ? 'branded-secondary' : 'neutral-secondary'}>
                                {(() => {
                                    const testsForLanguage = testLevelsByLanguage?.find(
                                        ({ language }) => language === selectedLanguage,
                                    )?.tests;
                                    const hasOnlyOneTest = testsForLanguage && testsForLanguage.length === 1;

                                    if (hasOnlyOneTest && selectedLevel) {
                                        return t('Level : {{levelName}}', { levelName: getTranslatedString(selectedLevel.title) });
                                    }

                                    return t('Level :');
                                })()}
                            </Typography>
                        </FlexBox>
                    )}
                    {(() => {
                        const testsForLanguage = testLevelsByLanguage?.find(({ language }) => language === selectedLanguage)?.tests;
                        const hasOnlyOneTest = testsForLanguage && testsForLanguage.length === 1;

                        // In booking mode a single level is stated in the "Level :" line
                        // above instead of a card; that line is hidden in read-only, so
                        // the card must render there or the section would be empty.
                        if (hasOnlyOneTest && !readOnly) {
                            return null;
                        }

                        return testsForLanguage
                            ?.sort((a, b) => a.priority - b.priority)
                            .map((level, idx) =>
                                level.description?.length > 0 ? (
                                    <div key={`${level.id}-${idx}`} className={style.levelsLine}>
                                        <Spoiler
                                            classNames={{
                                                description: style.levelsTextWrapper,
                                            }}
                                            maxLines={3}
                                            components={{
                                                Container: ({ children }) => (
                                                    <div
                                                        className={cx(style.levelsContainer, {
                                                            [style.selected]: level.id === highlightedLevelId,
                                                            [style.disabled]: !readOnly && isLevelAlreadySelected(level),
                                                            [style.descriptionContainer]: true,
                                                        })}
                                                        {...levelInteractionProps(level)}
                                                    >
                                                        {!readOnly && (
                                                            <InputRadio
                                                                checked={level.id === selectedLevel?.id}
                                                                disabled={isLevelAlreadySelected(level)}
                                                                onChange={(e) => e.stopPropagation()}
                                                            />
                                                        )}
                                                        {children}
                                                    </div>
                                                ),
                                                TextWrapper: ({ ref }) => (
                                                    // Since we do not use the default children we need to pass the ref so the Spoiler can still measure the height
                                                    <div ref={ref} style={{ fontSize: 'var(--font-s)' }}>
                                                        <Typography
                                                            fontSize="s"
                                                            fontWeight="semi-bold"
                                                            className={cx({ [style.levelsTextSelected]: level.id === highlightedLevelId })}
                                                        >
                                                            {getTranslatedString(level.title)}{' '}
                                                            {level.priority === 0 ? `(${t('recommended')})` : ''}
                                                            {level?.isPedagogical && (
                                                                <Tooltip
                                                                    text={t(
                                                                        'For each question, the pedagogical mode indicates whether the answer given is correct and provides explanatory elements about the correct answer(s), with the goal of understanding and learning.',
                                                                    )}
                                                                >
                                                                    <div
                                                                        className={cx(style.pedagogic, {
                                                                            [style.pedagogicSelected]: level.id === highlightedLevelId,
                                                                        })}
                                                                    >
                                                                        <Typography fontSize="s" fontWeight="regular">
                                                                            {t('Pedagogical')}
                                                                        </Typography>
                                                                        <FontAwesomeIcon icon={faCircleQuestion} />
                                                                    </div>
                                                                </Tooltip>
                                                            )}
                                                        </Typography>
                                                        <Typography fontSize="s" fontWeight="regular">
                                                            {getTranslatedString(level.description)}
                                                        </Typography>
                                                    </div>
                                                ),
                                                Button: ({ ...props }) => (
                                                    <Button
                                                        icon={props.icon}
                                                        noPadding
                                                        ghost
                                                        action={(e) => {
                                                            if (e && typeof e.stopPropagation === 'function') {
                                                                e.stopPropagation();
                                                            }
                                                            if (typeof props.onClick === 'function') {
                                                                props.onClick(e);
                                                            }
                                                        }}
                                                    />
                                                ),
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div key={`${level.id}-${idx}`} className={style.levelsLine}>
                                        <div
                                            className={cx(style.levelsContainer, {
                                                [style.selected]: level.id === highlightedLevelId,
                                                [style.disabled]: !readOnly && isLevelAlreadySelected(level),
                                                [style.descriptionContainer]: true,
                                            })}
                                            {...levelInteractionProps(level)}
                                        >
                                            {!readOnly && (
                                                <InputRadio
                                                    checked={level.id === selectedLevel?.id}
                                                    disabled={isLevelAlreadySelected(level)}
                                                    onChange={(e) => e.stopPropagation()}
                                                />
                                            )}
                                            <div className={style.levelsTextWrapper}>
                                                <Typography fontSize="s" fontWeight="semi-bold">
                                                    {getTranslatedString(level.title)} {level.priority === 0 ? `(${t('recommended')})` : ''}
                                                    {level?.isPedagogical && (
                                                        <Tooltip
                                                            text={t(
                                                                'For each question, the pedagogical mode indicates whether the answer given is correct and provides explanatory elements about the correct answer(s), with the goal of understanding and learning.',
                                                            )}
                                                        >
                                                            <div style={{ marginLeft: '4px' }}>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </div>
                                                        </Tooltip>
                                                    )}
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                ),
                            );
                    })()}
                </>
            )}

            {onConfirm && (
                <div className={style.actions}>
                    <Button label={t('Cancel')} className={style.cancel} action={onClose} ghost />
                    <Button
                        label={selectedTests.length && subtestId ? t('Save changes') : t('Add to evaluation')}
                        className={style.confirm}
                        action={() => onConfirm(selectedLevel, subtestId)}
                        disabled={
                            !selectedLevel ||
                            selectedTests.some((t) => t.id === selectedLevel.id) ||
                            disabled ||
                            selectedLevel.id === subtestId
                        }
                    />
                </div>
            )}
        </div>
    );
};

export default TestSheet;
